import { BaseComponent } from '../core/BaseComponent.js';

export class RequestUploadModal extends BaseComponent {
    constructor(selector = '#request_upload_modal', options = {}) {
        super(selector, options);

        this.closeButtonSelector = '.close_modal, .cancel_button';
        this.formSelector = '#request-upload-form';

        this.apiAction = 'submit_upload_file_submission';
        this.ajaxUrl = window.docuswiftPortal?.ajax_url || '/wp-admin/admin-ajax.php';
        this.activeRequestId = null;
        this.prePay = null;
        this.postPay = null;
        this.isSubmitting = false;
    }

    init() {
        if (!super.init()) return this;

        this.cacheElements();
        this.bindEvents();

        return this;
    }

    cacheElements() {
        this.form = this.$(this.formSelector);
        this.closeButtons = this.$$(this.closeButtonSelector);


        // Set hidden prePay input if exists
        const prePayInput = this.form?.querySelector('[name="prepay"]');

        if (prePayInput) {
            prePayInput.value = this.prePay;
        }

        // Set hidden postPay input if exists
        const postPayInput = this.form?.querySelector('[name="postpay"]');
        if (postPayInput) {
            postPayInput.value = this.postPay;
        }

        
    }

    bindEvents() {
        if (!this.form) return;

        // Close modal
        this.closeButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                this.close();
            });
        });
        
        // Prevent double submit

        this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    open(requestId, data = {}) {
        this.activeRequestId = requestId;
        this.prePay = data.prePay || null;
        this.postPay = data.postPay || null;
        // Set hidden input if exists
        const input = this.form?.querySelector('[name="request_id"]');
        if (input) {
            input.value = requestId;

            
        }

        // Set hidden prePay input if exists
        const prePayInput = this.form?.querySelector('[name="prepay"]');
        if (prePayInput) {
            prePayInput.value = this.prePay;
        }

        // Set hidden postPay input if exists
        const postPayInput = this.form?.querySelector('[name="postpay"]');
        if (postPayInput) {
            postPayInput.value = this.postPay;
        }
        

        this.hideFeedback();
        this.fadeIn(this.element);
    }

    close() {
        this.fadeOut(this.element);
        this.activeRequestId = null;
        this.resetForm();
    }

    resetForm() {
        if (!this.form) return;
        this.form.reset();

        // Reset submit button loading state.
        const submitBtn = this.form.querySelector('[type="submit"]');
        this.setBtnLoading(submitBtn, false);

        // Reset file upload UI via the FileUpload component's own logic.
        const fileInput = this.form.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Reset Nice Select dropdowns if present.
        this.form.querySelectorAll('select').forEach((select) => {
            const niceEl = select.closest('.nice-select-wrapper, .nice_select_wrapper')
                        || (select.nextElementSibling?.classList.contains('nice-select') ? select.nextElementSibling : null);
            if (niceEl) {
                const current = niceEl.querySelector('.current');
                if (current) current.textContent = select.options[0]?.text || '';
            }
            select.dispatchEvent(new Event('change'));
        });

        this.hideFeedback();
    }

    setLoading(isLoading) {
        this.element.classList.toggle('loading', isLoading);
    }

    setBtnLoading(btn, isLoading) {
        if (!btn) return;
        if (isLoading) {
            btn.dataset.origText = btn.innerHTML;
            btn.classList.add('loading');
            btn.disabled = true;
            btn.style.pointerEvents = 'none';
        } else {
            btn.classList.remove('loading');
            btn.disabled = false;
            btn.style.pointerEvents = '';
            if (btn.dataset.origText) {
                btn.innerHTML = btn.dataset.origText;
                delete btn.dataset.origText;
            }
        }
    }

    fadeIn(el) {
        el.style.display = 'block';
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';

        requestAnimationFrame(() => {
            el.style.opacity = 1;
        });
    }

    fadeOut(el) {
        el.style.opacity = 0;
        el.style.transition = 'opacity 0.2s';

        setTimeout(() => {
            el.style.display = 'none';
        }, 200);
    }

    showFeedback(message, type = 'success') {
        let feedback = this.element.querySelector('.upload-feedback');
        if (!feedback) {
            feedback = document.createElement('div');
            feedback.className = 'upload-feedback';
            this.element.querySelector('.modal_content')?.appendChild(feedback);
        }
        feedback.textContent = message;
        feedback.className = `upload-feedback upload-feedback--${type}`;
        feedback.hidden = false;
    }

    hideFeedback() {
        const feedback = this.element?.querySelector('.upload-feedback');
        if (feedback) feedback.hidden = true;
    }

    async handleSubmit(e) {
        e.preventDefault();

        const submitBtn = this.form.querySelector('[type="submit"]');
        const fileInput  = this.form.querySelector('input[type="file"]');
        const files      = fileInput?.files ? Array.from(fileInput.files) : [];

        if (!files.length) {
            this.showFeedback('Please select at least one file.', 'error');
            return;
        }

        this.setBtnLoading(submitBtn, true);
        this.hideFeedback();

        const total    = files.length;
        let succeeded  = 0;
        const failed   = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            if (total > 1) {
                this.showFeedback(`Uploading ${i + 1} of ${total}: ${file.name}…`, 'info');
            }

            try {
                const formData = new FormData();
                formData.append('action', this.apiAction);
                formData.append('nonce', docuswiftPortal.nonce);
                formData.append('request_file_upload', file, file.name);

                // Copy all other form fields (request_id, file_category, file_status, etc.)
                for (const [key, value] of new FormData(this.form).entries()) {
                    if (key !== 'request_file_upload') {
                        formData.append(key, value);
                    }
                }

                const response = await fetch(docuswiftPortal.ajax_url, {
                    method: 'POST',
                    body: formData,
                });

                const result = await response.json();

                if (result.success) {
                    succeeded++;
                } else {
                    failed.push(`${file.name}: ${result.data?.message || 'Upload failed'}`);
                }
            } catch (error) {
                console.error('Upload error:', error);
                failed.push(`${file.name}: Network error`);
            }
        }

        this.setBtnLoading(submitBtn, false);

        if (failed.length === 0) {
            const msg = total > 1
                ? `${succeeded} file${succeeded > 1 ? 's' : ''} uploaded successfully.`
                : 'File uploaded successfully.';
            this.showFeedback(msg, 'success');
            this.form.reset();
            document.dispatchEvent(new CustomEvent('request:file-uploaded', {
                detail: { requestId: this.activeRequestId }
            }));
            setTimeout(() => this.close(), 2000);
        } else if (succeeded > 0) {
            this.showFeedback(
                `${succeeded} uploaded, ${failed.length} failed: ${failed.join('; ')}`,
                'error'
            );
            document.dispatchEvent(new CustomEvent('request:file-uploaded', {
                detail: { requestId: this.activeRequestId }
            }));
        } else {
            this.showFeedback(failed.join('; '), 'error');
        }
    }
}